import { appToast } from "@components/Toast";
import { IdentitySwitchMenu } from "@components/IdentitySwitchMenu";
import { track } from "@lib/tracker";
import type { GasCylinder, LastOrderGasItem } from "@screen/Home/pages/GasShop/products";
import { filterGasByMortgage } from "@screen/Home/pages/GasShop/products";
import { isOrderableGasType } from "@utils/func/gasTitle";
import SupplierIdentityBar from "@components/SupplierIdentityBar";
import { hasReservableSlots } from "@utils/func/deliverySlots";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useAuthReady } from "@provider/LineLoginProvider";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ScrollView, Text, YStack } from "tamagui";
import { reorderFromLast } from "./api/kioskApi";
import AddressPickerSheet from "./components/AddressPickerSheet";
import FloatingCartBar from "./components/FloatingCartBar";
import HomeAddressCard from "./components/HomeAddressCard";
import InstallSheet from "./components/InstallSheet";
import { KioskHeader } from "./components/KioskParts";
import ProductGrid from "./components/ProductGrid";
import ReorderCard from "./components/ReorderCard";
import SupplierSelectSheet from "./components/SupplierSelectSheet";
import ConfirmAddressSheet from "./components/ConfirmAddressSheet";
import "./kiosk.scss";
import { flyToCart } from "./kioskFx";
import { kiosk } from "./kioskTheme";
import { useKiosk } from "./useKiosk";
import { useKioskBootstrap } from "./useKioskBootstrap";

const KioskHome = () => {
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const { productList, deliverySlots, allowReservation, cartProductTotalPrice, supplierIdentity } = useOrder();
  const { setLoading } = useUtilsStore();
  const authReady = useAuthReady();
  const { setFirstCheckoutDone, needSupplierSelect, setNeedSupplierSelect, setHidePrice, needIdentityCheck, pendingReview, entryAddress } = useKiosk();
  // 新客戶/待核對（首次加入資料尚未完成）沒有「上次訂購」可重訂 → 不顯示重訂卡。
  const isFirstTimeCustomer = needIdentityCheck || pendingReview;
  const location = useLocation();

  // 進 kiosk 決定是否隱藏價格（顯示「專員報價」）；一般既有客戶恢復顯示金額，不受上次殘值影響。
  // - biz=1：營業客戶
  // - 掃碼/新客戶（needIdentityCheck / pendingReview）：尚未定價，一路到結算頁都不顯示任何價格
  useEffect(() => {
    setHidePrice(new URLSearchParams(location.search).get("biz") === "1" || isFirstTimeCustomer);
  }, [location.search, setHidePrice, isFirstTimeCustomer]);

  // 登入完成後關掉全域鋼瓶；進頁讀取改由 ProductGrid 頁內 spinner，避免兩個 loading 疊加
  useEffect(() => {
    if (authReady) setLoading(false);
  }, [authReady, setLoading]);

  // QR 進入：不跳全屏「完成加入設定」。新客戶/待核對的身分核對＋鋼瓶/配件/合約，
  // 統一在「一鍵下單 → 結算頁（confirm）」就地以卡片完成（見 confirm.tsx）。

  const { supplier, loaded, supplierId, xContext } = useKioskBootstrap();
  const [installProduct, setInstallProduct] = useState<GasCylinder | null>(null);
  const [installSeedQty, setInstallSeedQty] = useState<number | undefined>(undefined); // 點「上次訂購」標示的瓦斯 → 預帶上次桶數
  const [showAddr, setShowAddr] = useState(false);
  const [addrStartAdd, setAddrStartAdd] = useState(false); // 開地址表單即進新增（無既有地址）
  const [addrAutoLocate, setAddrAutoLocate] = useState(false); // 開新增表單即自動 GPS 定位
  const [addrConfirmed, setAddrConfirmed] = useState(false); // 新客戶進 kiosk 自動彈地址表單/確認地址只彈一次
  const [showConfirmAddr, setShowConfirmAddr] = useState(false); // 定位帶入地址 → 進 kiosk 彈「確認送達地址」
  const [badgePulse, setBadgePulse] = useState(false);

  // 允許定位已移到 QR·掃 barcode 那關（onboarding）；kiosk 只保留手動地址表單。
  const closeAddr = () => {
    setShowAddr(false);
    setAddrStartAdd(false);
    setAddrAutoLocate(false);
  };

  // 舊 demo 佔位（demo-gps）殘留 → 同樣彈「確認送達地址」重新定位建真實地址（demo 也接真 API，不再假預填）。
  const isDemoGpsAddr = chooseIdentity?.choose_address?.customer_address_id === "demo-gps";
  useEffect(() => {
    if (loaded && !addrConfirmed && isDemoGpsAddr) {
      setAddrConfirmed(true);
      setShowConfirmAddr(true);
    }
  }, [loaded, addrConfirmed, isDemoGpsAddr]);

  // QR 流程的新客戶進一鍵下單 → 自動彈地址 modal 設定送達地址（原切版行為，2026-07-17 版型對齊）。
  // 沒有地址（或佔位 CIS 的空地址）→ 彈「確認送達地址」sheet（onboarding 填寫配送地址同版型：
  // 地址/門牌/樓層/配送指示/地圖微調），autoLocate 自動 GPS 定位帶入，送出建真實地址；
  // 已有真地址 → 開地址選單讓客戶確認/改填。只彈一次（addrConfirmed）；既有客戶不彈。
  useEffect(() => {
    if (!loaded || addrConfirmed || !isFirstTimeCustomer) return;
    setAddrConfirmed(true);
    const identityAddr = chooseIdentity?.choose_address?.full_address?.trim();
    // onboarding 的地址關已經收過地址（entryAddress）→ 不要再彈一次地址匡，客戶剛填完。
    // 但必須「身分上真的有地址」才跳過：送單要帶 customer_address_id，
    // 若後端還沒把地址建起來就跳過，客戶會走到結算頁才發現沒地址、更難處理。
    if (entryAddress?.trim() && identityAddr) return;
    if (!identityAddr) {
      setShowConfirmAddr(true);
    } else {
      setShowAddr(true);
    }
  }, [loaded, addrConfirmed, isFirstTimeCustomer, chooseIdentity?.choose_address?.full_address, entryAddress]);

  // 先濾掉非瓦斯氣別（丙烷／純丙烷），再依押桶紀錄過濾公斤數（後端未帶 mortgage_kilograms 時顯示全部）。
  // 與訂購頁完全一致：不排除 cis_gas——客戶押桶的公斤數其瓦斯品項可能正是 cis_gas（合約價），
  // 排除會導致該公斤數無任何瓦斯可選（例：押 20KG 但 20KG 液化氣為 cis_gas）。
  const gasList = filterGasByMortgage(
    (supplier?.gas_cylinders || []).filter((g) => isOrderableGasType(g.gas_type)),
    supplier?.mortgage_kilograms,
  );
  const lastOrder = supplier?.last_order || null;
  const isOpenNow = supplier?.is_open_now ?? true;
  // 可預約 = 瓦斯行開放線上預約(allowReservation) 且 有可預約時段。未開放預約時 reservable 一律 false。
  // 公休但仍可預約時照常開放下單（只能走預約）；公休又未開放預約 → 完全不可下單（尚未營業）。
  const reservable = allowReservation && hasReservableSlots(deliverySlots);
  const canOrder = isOpenNow || reservable;
  const closedButReservable = !isOpenNow && reservable;
  const address = chooseIdentity?.choose_address?.full_address || "尚未設定地址";
  const cartCount = productList.reduce((s, i) => s + i.qty, 0);

  useEffect(() => {
    if (loaded && !canOrder) {
      track("order_blocked_not_open", { surface_context: "kiosk" });
    }
  }, [loaded, canOrder]);

  const lastItemFor = (gasId: number): LastOrderGasItem | null =>
    lastOrder?.gas_list?.find((it) => it.gas_id === gasId && it.install_location) || null;

  // QR 入口特異化（僅本次由 onboarding 掃 QR 綁定後導進來時，URL 帶 src=qr）：
  // 不顯示「一鍵重新訂購」卡，改在型錄把上次訂購的瓦斯標示出來，點進去走既有安裝精靈補位置。
  // 用 URL 判定而非 useKiosk.entrySource——後者存 sessionStorage 且不會清除，同分頁之後從首頁
  // 進 kiosk 會殘留成 "qr"，導致一般 kiosk 也誤顯示標示。
  const isQrEntry = new URLSearchParams(location.search).get("src") === "qr";
  // 上次訂購各瓦斯的總桶數（同一 gas_id 多筆歷史合併，例：20KG ×2 + ×1 → ×3）
  const lastOrderQtyByGasId = useMemo(() => {
    const map = new Map<number, number>();
    if (!isQrEntry) return map; // 一般 kiosk 不標示
    for (const it of lastOrder?.gas_list ?? []) {
      if (it.gas_id == null) continue;
      const key = Number(it.gas_id);
      map.set(key, (map.get(key) ?? 0) + (it.qty || 0));
    }
    return map;
  }, [isQrEntry, lastOrder]);

  // 點型錄卡片：若是「上次訂購」標示的那款 → 安裝精靈預帶上次桶數；其餘照常從 1 開始。
  const handlePick = (p: GasCylinder) => {
    const seed = lastOrderQtyByGasId.get(Number(p.gas_id));
    setInstallSeedQty(seed && seed > 0 ? seed : undefined);
    setInstallProduct(p);
  };

  const handleReorder = async () => {
    if (!lastOrder || !supplierId) return;
    const addrId = chooseIdentity?.choose_address?.customer_address_id;
    if (!addrId) {
      appToast.error("請先設定送達地址");
      return;
    }
    track("entry_click", { entry_source: "kiosk_reorder" });
    setLoading(true);
    try {
      const draft = await reorderFromLast({
        lastOrder,
        supplierId,
        customerAddressId: addrId,
        jwtToken: cusJwtToken || "",
        xContext,
      });
      if (draft) {
        useOrder.getState().syncDraft(draft);
        // 一鍵重訂的新草稿預設立即配送，不沿用上一筆訂單的預約時段/slot；
        // 但若供應商目前公休（僅能預約），預設改為預約配送，避免帶入無法成立的立即配送。
        useOrder.getState().setDeliveryTime({ type: isOpenNow ? "immediate" : "scheduled", date: null, slotId: null, slotSource: null, slotLabel: null });
        setFirstCheckoutDone(true);
        const cnt = (draft.gas_list || []).reduce((s: number, i: any) => s + (i.qty || 0), 0);
        appToast.success(`已帶入上次訂單 · 共 ${cnt} 件`);
        navigate("/kiosk/cart");
      } else {
        appToast.error("帶入上次訂單失敗");
      }
    } finally {
      setLoading(false);
    }
  };

  const onAdded = () => {
    track("funnel_step", { step: "products" });
    flyToCart(() => {
      setBadgePulse(true);
      setTimeout(() => setBadgePulse(false), 500);
      appToast.success("已加入購物車");
    });
  };

  return (
        // 底色沿用全站的淡霧（.gas-skin-v2-bg，定義在 all.scss）而不是複製一份：
    // 兩份定義遲早會走鐘，而客戶在同一個 App 裡會前後看到這兩條流程。
    <YStack flex={1} position="relative" className="gas-skin-v2-bg">
      <KioskHeader
        title="一鍵瓦斯訂購"
        // 賣方身分（名稱／統編／撥打）：下單前讓客戶看清楚交易對象。由 bootstrap 寫入 store，未載入時不佔位。
        subtitle={supplierIdentity ? <SupplierIdentityBar identity={supplierIdentity} align="start" /> : undefined}
        right={<IdentitySwitchMenu />}
      />

      <ScrollView flex={1} className="kiosk-noscroll kiosk-stagger" contentContainerStyle={{ paddingTop: 8, paddingLeft: 18, paddingRight: 18, paddingBottom: 110 }}>
        <HomeAddressCard address={address} onChange={() => { setAddrStartAdd(false); setAddrAutoLocate(false); setShowAddr(true); }} />
        {/* QR 入口不顯示重訂卡（改用型錄上的「上次訂購」標示）；一般 kiosk 維持原本行為 */}
        {!isFirstTimeCustomer && !isQrEntry && <ReorderCard lastOrder={lastOrder} onReorder={handleReorder} />}

        <Text fontSize={13} fontWeight="800" mb={14} color={kiosk.ink} letterSpacing={-0.2}>
          桶裝瓦斯
        </Text>
        <ProductGrid
          loaded={loaded}
          canOrder={canOrder}
          closedButReservable={closedButReservable}
          products={gasList}
          lastOrderQtyByGasId={lastOrderQtyByGasId}
          onPick={handlePick}
        />
      </ScrollView>

      <FloatingCartBar count={cartCount} total={cartProductTotalPrice} pulse={badgePulse} onPress={() => navigate("/kiosk/cart")} />

      {/* overlays */}
      {installProduct && (
        <InstallSheet
          product={installProduct}
          lastItem={lastItemFor(installProduct.gas_id)}
          initialQty={installSeedQty}
          onClose={() => {
            setInstallProduct(null);
            setInstallSeedQty(undefined);
          }}
          onAdded={onAdded}
        />
      )}
      <AddressPickerSheet isOpen={showAddr} onClose={closeAddr} startInAdd={addrStartAdd} autoLocate={addrAutoLocate} />

      {/* 定位帶入地址 → 進 kiosk 彈「確認送達地址」（沿用填寫配送地址設計，可直接修改） */}
      <ConfirmAddressSheet open={showConfirmAddr} onClose={() => setShowConfirmAddr(false)} autoLocate />

      {/* 新客戶/未匹配到瓦斯行 → 下單前先選瓦斯行 */}
      <SupplierSelectSheet
        isOpen={needSupplierSelect}
        onPick={() => {
          // TODO: 綁定所選瓦斯行到帳戶（primary_supplier）；demo 先清旗標
          setNeedSupplierSelect(false);
          appToast.success("已選擇瓦斯行");
        }}
      />
    </YStack>
  );
};

export default KioskHome;
